import {
	createContext,
	FC,
	ReactNode,
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useRef,
	useState,
} from 'react'
import { useLocation } from 'react-router-dom'
import { publicRoutes } from '@/shared/constants/publicRoutes'
import {
	AUTH_LOGOUT_PENDING_KEY,
	authTokenStorage,
	type AuthTokenRevision,
	type AuthTokenSet,
	type StoredAuthTokens,
} from '@shared/lib/authTokenStorage'
import { apiService, type EnsureFreshSession } from '@shared/api/auth-instance'
import { KcProxy } from '@/shared/api/kcProxy'
import { CustomLoader } from '@/shared/animations/loaders/CustomLoader'
import { ROUTES } from '@shared/constants/routes'
import { useIonRouter } from '@ionic/react'
import {
	clearPendingAuthRequest,
	getPendingAuthRequest,
	storeProgramAuthReturnPath,
} from '@shared/lib/authRedirect'
import { toast } from 'sonner'

export type AuthMode = 'public' | 'keycloak' | 'token'
export type AuthStatus =
	| 'public'
	| 'bootstrapping'
	| 'refreshing'
	| 'authenticated'
	| 'unauthenticated'

export type AuthModeState = {
	mode: AuthMode
	status: AuthStatus
	initialized: boolean
	authenticated: boolean
	accessToken: string | null
	refreshToken: string | null
	ensureFreshSession: EnsureFreshSession
	logout: () => Promise<void>
}

type RefreshSessionResult =
	| { status: 'available'; tokens: StoredAuthTokens }
	| { status: 'failed'; authEpoch: number; expectedRevision: AuthTokenRevision }
	| { status: 'refresh-unavailable' }
	| { status: 'storage-unavailable' }
	| { status: 'stale' }

const SESSION_EXPIRED_MESSAGE = 'Сессия истекла. Войдите снова.'
const AUTH_ROUTE_FALLBACK_DELAY_MS = 500
const SESSION_RECOVERY_RETRY_DELAY_MS = 3_000
const TOKEN_EXPIRY_LEEWAY_MS = 30_000

const isTerminalOAuthTokenFailure = (error: unknown): boolean => {
	if (!error || typeof error !== 'object') return false
	const candidate = error as { name?: unknown; kind?: unknown }
	return candidate.name === 'OAuthTokenRequestError' && candidate.kind === 'terminal'
}

const isExplicitExpiryReached = (expiresAt: number): boolean =>
	Date.now() + TOKEN_EXPIRY_LEEWAY_MS >= expiresAt

const isAccessTokenUsable = (tokens: StoredAuthTokens | null | undefined): boolean => {
	if (!tokens?.accessToken) return false
	return tokens.accessExpiresAt === null
		? !authTokenStorage.isAccessTokenExpired(tokens.accessToken)
		: !isExplicitExpiryReached(tokens.accessExpiresAt)
}

const isRefreshTokenUsable = (tokens: StoredAuthTokens | null | undefined): boolean => {
	if (!tokens?.refreshToken) return false
	return tokens.refreshExpiresAt === null
		? !authTokenStorage.isRefreshTokenExpired(tokens.refreshToken)
		: !isExplicitExpiryReached(tokens.refreshExpiresAt)
}

const AuthModeContext = createContext<AuthModeState>({
	mode: 'public',
	status: 'public',
	initialized: false,
	authenticated: false,
	accessToken: null,
	refreshToken: null,
	ensureFreshSession: async () => null,
	logout: async () => undefined,
})

export const useAuthMode = (): AuthModeState => useContext(AuthModeContext)

export const AuthModeProvider: FC<{ children: ReactNode }> = ({ children }) => {
	const location = useLocation()
	const nav = useIonRouter()
	const isPublicRoute = publicRoutes.includes(location.pathname)
	const [storedTokens, setStoredTokens] = useState<StoredAuthTokens | null | undefined>(undefined)
	const [refreshStatus, setRefreshStatus] = useState<'idle' | 'refreshing' | 'failed'>('idle')
	const [callbackStatus, setCallbackStatus] = useState<'idle' | 'handling' | 'done'>('idle')
	const [storageRetryNonce, setStorageRetryNonce] = useState(0)
	const latestStoredTokensRef = useRef<StoredAuthTokens | null | undefined>(storedTokens)
	const refreshPromiseRef = useRef<Promise<RefreshSessionResult> | null>(null)
	const authEpochRef = useRef(0)
	const authoritativeReadSequenceRef = useRef(0)
	const localClearInProgressRef = useRef(0)
	const storageRetryTimerRef = useRef<number | null>(null)
	const sessionFailureNotifiedRef = useRef(false)
	const isAuthCallbackRoute = location.pathname === ROUTES.auth.enterBaseInfo
	const [callbackHash, setCallbackHash] = useState<string>(() =>
		typeof window === 'undefined' ? '' : window.location.hash
	)
	const callbackHashParams = useMemo(
		() => new URLSearchParams(callbackHash.startsWith('#') ? callbackHash.slice(1) : ''),
		[callbackHash]
	)
	const hasAuthCallbackParams = useMemo(
		() =>
			isAuthCallbackRoute &&
			(callbackHashParams.has('code') ||
				callbackHashParams.has('error') ||
				callbackHashParams.has('state')),
		[callbackHashParams, isAuthCallbackRoute]
	)
	const isProcessingAuthCallback = hasAuthCallbackParams || callbackStatus === 'handling'

	const updateStoredTokens = useCallback((tokens: StoredAuthTokens | null | undefined): void => {
		latestStoredTokensRef.current = tokens
		setStoredTokens(tokens)
	}, [])

	const cancelStorageRetry = useCallback((): void => {
		if (storageRetryTimerRef.current === null) return
		window.clearTimeout(storageRetryTimerRef.current)
		storageRetryTimerRef.current = null
	}, [])

	const scheduleStorageRetry = useCallback((): void => {
		if (storageRetryTimerRef.current !== null) return
		storageRetryTimerRef.current = window.setTimeout(() => {
			storageRetryTimerRef.current = null
			setRefreshStatus(current => (current === 'failed' ? current : 'idle'))
			setStorageRetryNonce(value => value + 1)
		}, SESSION_RECOVERY_RETRY_DELAY_MS)
	}, [])

	useEffect(() => cancelStorageRetry, [cancelStorageRetry])

	const readAuthoritativeTokens = useCallback(async (): Promise<StoredAuthTokens | null> => {
		const sequence = ++authoritativeReadSequenceRef.current
		const tokens = await authTokenStorage.getTokens()
		cancelStorageRetry()
		if (sequence === authoritativeReadSequenceRef.current) {
			updateStoredTokens(tokens)
		}
		return tokens
	}, [cancelStorageRetry, updateStoredTokens])

	const clearPersistedTokens = useCallback(async (): Promise<void> => {
		authEpochRef.current += 1
		apiService.clearToken()
		localClearInProgressRef.current += 1
		try {
			await authTokenStorage.clearTokens()
		} catch {
			console.error('Failed to clear auth tokens')
		} finally {
			localClearInProgressRef.current -= 1
		}
	}, [])

	const clearPersistedTokensIfCurrent = useCallback(
		async (expected: AuthTokenRevision): Promise<'cleared' | 'stale' | 'failed'> => {
			authEpochRef.current += 1
			apiService.clearToken()
			localClearInProgressRef.current += 1
			try {
				return (await authTokenStorage.clearTokensIfCurrent(expected)) ? 'cleared' : 'stale'
			} catch {
				console.error('Failed to conditionally clear auth tokens')
				return 'failed'
			} finally {
				localClearInProgressRef.current -= 1
			}
		},
		[]
	)

	const pushAuthEntryRoute = useCallback((): void => {
		nav.push(ROUTES.overall.hello)
		window.setTimeout(() => {
			if (window.location.pathname !== ROUTES.overall.hello) {
				window.location.replace(ROUTES.overall.hello)
			}
		}, AUTH_ROUTE_FALLBACK_DELAY_MS)
	}, [nav])

	const persistAuthenticatedTokens = useCallback(
		(
			expected: { generation: number; revision: number },
			tokens: AuthTokenSet
		): Promise<StoredAuthTokens | null> =>
			authTokenStorage.saveNewSessionIfCurrent(expected, tokens),
		[]
	)

	const finishUnauthenticatedSession = useCallback(
		async (
			showExpiredMessage: boolean,
			expectedRevision?: AuthTokenRevision
		): Promise<boolean> => {
			if (expectedRevision) {
				const clearResult = await clearPersistedTokensIfCurrent(expectedRevision)
				if (clearResult === 'failed') {
					setRefreshStatus('refreshing')
					scheduleStorageRetry()
					return false
				}
				if (clearResult === 'stale') {
					try {
						const latestTokens = await readAuthoritativeTokens()
						if (latestTokens) {
							setRefreshStatus('idle')
							sessionFailureNotifiedRef.current = false
							return false
						}
					} catch {
						setRefreshStatus('refreshing')
						scheduleStorageRetry()
						return false
					}
				}
			} else {
				await clearPersistedTokens()
			}

			storeProgramAuthReturnPath(`${location.pathname}${location.search}${location.hash}`)
			setRefreshStatus('failed')
			updateStoredTokens(null)
			clearPendingAuthRequest()

			if (showExpiredMessage && !sessionFailureNotifiedRef.current) {
				sessionFailureNotifiedRef.current = true
				toast.error(SESSION_EXPIRED_MESSAGE)
			}

			pushAuthEntryRoute()
			return true
		},
		[
			clearPersistedTokens,
			clearPersistedTokensIfCurrent,
			location.hash,
			location.pathname,
			location.search,
			pushAuthEntryRoute,
			readAuthoritativeTokens,
			scheduleStorageRetry,
			updateStoredTokens,
		]
	)

	const finishRemoteUnauthenticatedSession = useCallback((): void => {
		authEpochRef.current += 1
		apiService.clearToken()
		setRefreshStatus('failed')
		updateStoredTokens(null)
		clearPendingAuthRequest()

		if (isPublicRoute) return
		storeProgramAuthReturnPath(`${location.pathname}${location.search}${location.hash}`)
		if (!sessionFailureNotifiedRef.current) {
			sessionFailureNotifiedRef.current = true
			toast.error(SESSION_EXPIRED_MESSAGE)
		}
		pushAuthEntryRoute()
	}, [
		isPublicRoute,
		location.hash,
		location.pathname,
		location.search,
		pushAuthEntryRoute,
		updateStoredTokens,
	])

	const logout = useCallback(async (): Promise<void> => {
		sessionFailureNotifiedRef.current = false
		await finishUnauthenticatedSession(false)
	}, [finishUnauthenticatedSession])

	const ensureFreshSession: EnsureFreshSession = useCallback(
		async options => {
			let tokens = latestStoredTokensRef.current
			if (tokens === undefined || options?.forceRefresh || !isAccessTokenUsable(tokens)) {
				try {
					// Expiry, a 401, and startup cross the persistence boundary. The normal
					// valid-token path remains an in-memory timestamp check.
					tokens = await readAuthoritativeTokens()
				} catch {
					// A transient storage failure must not install a logout tombstone over
					// credentials that may still be intact in IndexedDB or secure storage.
					console.error('Auth token storage is temporarily unavailable')
					setRefreshStatus('refreshing')
					scheduleStorageRetry()
					return null
				}
			}

			const hadCredentials = Boolean(tokens?.accessToken || tokens?.refreshToken)
			if (
				tokens &&
				isAccessTokenUsable(tokens) &&
				(!options?.forceRefresh ||
					Boolean(
						options.rejectedAccessToken &&
							tokens.accessToken !== options.rejectedAccessToken
					))
			) {
				return tokens.accessToken
			}

			if (!tokens || !isRefreshTokenUsable(tokens) || !tokens.refreshToken) {
				const expectedRevision = tokens
					? { generation: tokens.generation, revision: tokens.revision }
					: undefined
				await finishUnauthenticatedSession(hadCredentials, expectedRevision)
				return null
			}

			if (!refreshPromiseRef.current) {
				setRefreshStatus('refreshing')
				const refreshEpoch = authEpochRef.current
				const refreshOptions = { ...options }
				const refreshPromise = authTokenStorage
					.runExclusiveRefresh<RefreshSessionResult>(async () => {
						if (authEpochRef.current !== refreshEpoch) return { status: 'stale' }

						// Web Locks protects the cross-tab section. Re-read after acquiring it;
						// another tab may already have rotated the refresh token while we waited.
						const lockedTokens = await readAuthoritativeTokens()
						if (authEpochRef.current !== refreshEpoch) return { status: 'stale' }

						if (!lockedTokens) return { status: 'stale' }
						const canReuseLockedAccessToken =
							isAccessTokenUsable(lockedTokens) &&
							(!refreshOptions.forceRefresh ||
								Boolean(
									refreshOptions.rejectedAccessToken &&
										lockedTokens.accessToken !==
											refreshOptions.rejectedAccessToken
								))
						if (canReuseLockedAccessToken) {
							return { status: 'available', tokens: lockedTokens }
						}

						if (!isRefreshTokenUsable(lockedTokens) || !lockedTokens.refreshToken) {
							return {
								status: 'failed',
								authEpoch: refreshEpoch,
								expectedRevision: {
									generation: lockedTokens.generation,
									revision: lockedTokens.revision,
								},
							}
						}

						const expectedRevision = {
							generation: lockedTokens.generation,
							revision: lockedTokens.revision,
						}
						let nextTokens
						try {
							nextTokens = await KcProxy.refreshTokens(lockedTokens.refreshToken)
						} catch (error) {
							return isTerminalOAuthTokenFailure(error)
								? {
										status: 'failed',
										authEpoch: refreshEpoch,
										expectedRevision,
									}
								: { status: 'refresh-unavailable' }
						}
						if (authEpochRef.current !== refreshEpoch) return { status: 'stale' }
						if (!nextTokens?.accessToken) {
							return {
								status: 'failed',
								authEpoch: refreshEpoch,
								expectedRevision,
							}
						}

						const refreshTokenWasRotated = nextTokens.refreshToken !== null
						const refreshedTokens = await authTokenStorage.compareAndSetRefresh(
							expectedRevision,
							{
								accessToken: nextTokens.accessToken,
								refreshToken: nextTokens.refreshToken ?? lockedTokens.refreshToken,
								receivedAt: nextTokens.receivedAt,
								accessExpiresAt: nextTokens.accessExpiresAt,
								refreshExpiresAt:
									nextTokens.refreshExpiresAt ??
									(refreshTokenWasRotated ? null : lockedTokens.refreshExpiresAt),
							}
						)
						if (authEpochRef.current !== refreshEpoch) return { status: 'stale' }

						if (refreshedTokens) {
							updateStoredTokens(refreshedTokens)
							return isAccessTokenUsable(refreshedTokens)
								? { status: 'available', tokens: refreshedTokens }
								: {
										status: 'failed',
										authEpoch: refreshEpoch,
										expectedRevision: {
											generation: refreshedTokens.generation,
											revision: refreshedTokens.revision,
										},
									}
						}

						// A stale CAS means another tab won or logout installed a tombstone.
						const latestTokens = await readAuthoritativeTokens()
						if (authEpochRef.current !== refreshEpoch) return { status: 'stale' }
						return latestTokens && isAccessTokenUsable(latestTokens)
							? { status: 'available', tokens: latestTokens }
							: { status: 'stale' }
					})
					.catch((): RefreshSessionResult => {
						console.error(
							'Auth session storage or coordination is temporarily unavailable'
						)
						return { status: 'storage-unavailable' }
					})

				const trackedPromise = refreshPromise.finally(() => {
					if (refreshPromiseRef.current === trackedPromise) {
						refreshPromiseRef.current = null
					}
				})
				refreshPromiseRef.current = trackedPromise
			}

			const refreshResult = await refreshPromiseRef.current
			if (refreshResult.status === 'available') {
				sessionFailureNotifiedRef.current = false
				setRefreshStatus('idle')
				return refreshResult.tokens.accessToken
			}

			if (refreshResult.status === 'failed') {
				if (authEpochRef.current === refreshResult.authEpoch) {
					const finished = await finishUnauthenticatedSession(
						true,
						refreshResult.expectedRevision
					)
					if (!finished) {
						const latestTokens = latestStoredTokensRef.current
						return latestTokens && isAccessTokenUsable(latestTokens)
							? latestTokens.accessToken
							: null
					}
				}
				return null
			}

			if (refreshResult.status === 'storage-unavailable') {
				setRefreshStatus('refreshing')
				scheduleStorageRetry()
				return null
			}

			if (refreshResult.status === 'refresh-unavailable') {
				setRefreshStatus('refreshing')
				scheduleStorageRetry()
				return null
			}

			setRefreshStatus('idle')
			try {
				const latestTokens = await readAuthoritativeTokens()
				return latestTokens && isAccessTokenUsable(latestTokens)
					? latestTokens.accessToken
					: null
			} catch {
				setRefreshStatus('refreshing')
				scheduleStorageRetry()
				return null
			}
		},
		[
			finishUnauthenticatedSession,
			readAuthoritativeTokens,
			scheduleStorageRetry,
			updateStoredTokens,
		]
	)

	useEffect(() => {
		if (typeof window === 'undefined') return
		setCallbackHash(window.location.hash)
	}, [location.pathname])

	useEffect(() => {
		if (!hasAuthCallbackParams) {
			setCallbackStatus('idle')
			return
		}

		let cancelled = false
		setCallbackStatus('handling')
		;(async () => {
			const finishRejectedCallback = (): void => {
				if (cancelled) return
				setCallbackHash('')
				setCallbackStatus('done')
				window.history.replaceState(null, '', location.pathname)
				pushAuthEntryRoute()
			}

			const callbackError = callbackHashParams.get('error')
			try {
				const code = callbackHashParams.get('code')
				const state = callbackHashParams.get('state')
				const pendingAuthRequest = state ? getPendingAuthRequest(state) : null

				// Unsolicited or mismatched callbacks must not be able to clear an
				// already-authenticated session. A mismatched state also leaves the valid
				// pending request intact for the real callback.
				if (!state || !pendingAuthRequest || callbackError || !code) {
					finishRejectedCallback()
					return
				}

				const callbackEpoch = authEpochRef.current
				const callbackBaseTokens = await readAuthoritativeTokens()
				if (authEpochRef.current !== callbackEpoch) {
					finishRejectedCallback()
					return
				}
				const callbackBaseRevision = callbackBaseTokens
					? {
							generation: callbackBaseTokens.generation,
							revision: callbackBaseTokens.revision,
						}
					: authTokenStorage.getCurrentRevision()
				const tokens = await KcProxy.exchangeAuthorizationCode({
					code,
					codeVerifier: pendingAuthRequest.codeVerifier,
					redirectUri: pendingAuthRequest.redirectUri,
				})

				if (!tokens?.accessToken || authEpochRef.current !== callbackEpoch) {
					finishRejectedCallback()
					return
				}

				if (cancelled) return
				const nextStoredTokens = await persistAuthenticatedTokens(callbackBaseRevision, {
					accessToken: tokens.accessToken,
					refreshToken: tokens.refreshToken ?? null,
					receivedAt: tokens.receivedAt,
					accessExpiresAt: tokens.accessExpiresAt,
					refreshExpiresAt: tokens.refreshExpiresAt,
				})
				if (cancelled) return
				if (!nextStoredTokens) {
					await readAuthoritativeTokens().catch(() => null)
					finishRejectedCallback()
					return
				}
				updateStoredTokens(nextStoredTokens)
				setRefreshStatus('idle')
				sessionFailureNotifiedRef.current = false
				setCallbackHash('')
				window.history.replaceState(null, '', location.pathname)
				setCallbackStatus('done')
			} catch {
				finishRejectedCallback()
			}
		})()

		return () => {
			cancelled = true
		}
	}, [
		callbackHashParams,
		hasAuthCallbackParams,
		location.pathname,
		persistAuthenticatedTokens,
		pushAuthEntryRoute,
		readAuthoritativeTokens,
		updateStoredTokens,
	])

	useEffect(() => {
		if (isProcessingAuthCallback || storedTokens !== undefined) return

		let cancelled = false
		const sequence = ++authoritativeReadSequenceRef.current
		;(async () => {
			try {
				const tokens = await authTokenStorage.getTokens()
				if (!cancelled && sequence === authoritativeReadSequenceRef.current) {
					updateStoredTokens(tokens)
				}
			} catch {
				if (!cancelled && sequence === authoritativeReadSequenceRef.current) {
					console.error('Auth token storage is temporarily unavailable')
					scheduleStorageRetry()
				}
			}
		})()
		return () => {
			cancelled = true
		}
	}, [
		isProcessingAuthCallback,
		scheduleStorageRetry,
		storageRetryNonce,
		storedTokens,
		updateStoredTokens,
	])

	useEffect(() => {
		let disposed = false

		const unsubscribe = authTokenStorage.subscribe(change => {
			const sequence = ++authoritativeReadSequenceRef.current
			if (change.type === 'logout') {
				if (localClearInProgressRef.current > 0) {
					updateStoredTokens(null)
				} else {
					finishRemoteUnauthenticatedSession()
				}
			}

			void authTokenStorage
				.getTokens()
				.then(tokens => {
					if (disposed || sequence !== authoritativeReadSequenceRef.current) return
					updateStoredTokens(tokens)
					if (tokens) {
						sessionFailureNotifiedRef.current = false
						setRefreshStatus('idle')
					}
				})
				.catch(() => {
					// The logout notification already failed closed above. For an update,
					// retain the previous in-memory view until authoritative storage recovers.
					scheduleStorageRetry()
				})
		})

		return () => {
			disposed = true
			unsubscribe()
		}
	}, [finishRemoteUnauthenticatedSession, scheduleStorageRetry, updateStoredTokens])

	useEffect(() => {
		if (typeof window === 'undefined') return
		let disposed = false

		const reconcileDurableSession = (): void => {
			const hadLocalSession = Boolean(latestStoredTokensRef.current)
			void readAuthoritativeTokens()
				.then(tokens => {
					if (!disposed && hadLocalSession && !tokens) {
						finishRemoteUnauthenticatedSession()
					}
				})
				.catch(() => scheduleStorageRetry())
		}

		const handleStorage = (event: StorageEvent): void => {
			if (event.key !== AUTH_LOGOUT_PENDING_KEY || event.newValue !== '1') return
			// localStorage is only a non-secret cross-tab fail-closed signal. The
			// IndexedDB tombstone remains authoritative and is reconciled below.
			finishRemoteUnauthenticatedSession()
			reconcileDurableSession()
		}
		const handleVisibilityChange = (): void => {
			if (document.visibilityState === 'visible') reconcileDurableSession()
		}

		window.addEventListener('storage', handleStorage)
		window.addEventListener('pageshow', reconcileDurableSession)
		document.addEventListener('visibilitychange', handleVisibilityChange)
		return () => {
			disposed = true
			window.removeEventListener('storage', handleStorage)
			window.removeEventListener('pageshow', reconcileDurableSession)
			document.removeEventListener('visibilitychange', handleVisibilityChange)
		}
	}, [finishRemoteUnauthenticatedSession, readAuthoritativeTokens, scheduleStorageRetry])

	const storedAccessToken = storedTokens?.accessToken ?? null
	const storedRefreshToken = storedTokens?.refreshToken ?? null
	const hasValidStoredAccessToken = isAccessTokenUsable(storedTokens ?? null)
	const hasValidStoredRefreshToken = isRefreshTokenUsable(storedTokens ?? null)

	useEffect(() => {
		if (isPublicRoute || isProcessingAuthCallback) return
		if (storedTokens === undefined) return
		if (refreshStatus === 'failed') return
		if (hasValidStoredAccessToken) return
		if (refreshStatus === 'refreshing') return

		if (!hasValidStoredRefreshToken || !storedRefreshToken) {
			void finishUnauthenticatedSession(Boolean(storedAccessToken || storedRefreshToken))
			return
		}

		void ensureFreshSession()
	}, [
		ensureFreshSession,
		finishUnauthenticatedSession,
		hasValidStoredAccessToken,
		hasValidStoredRefreshToken,
		isPublicRoute,
		isProcessingAuthCallback,
		refreshStatus,
		storageRetryNonce,
		storedAccessToken,
		storedRefreshToken,
		storedTokens,
	])

	// Public routes render without auth initialization.
	if (isPublicRoute) {
		return (
			<AuthModeContext.Provider
				value={{
					mode: 'public',
					status: 'public',
					initialized: true,
					authenticated: false,
					accessToken: null,
					refreshToken: null,
					ensureFreshSession,
					logout,
				}}
			>
				{children}
			</AuthModeContext.Provider>
		)
	}

	if (isProcessingAuthCallback) {
		return <CustomLoader />
	}

	// Load secure storage before deciding between refresh vs Keycloak redirects.
	if (storedTokens === undefined) {
		return <CustomLoader />
	}

	if (!hasValidStoredAccessToken && !hasValidStoredRefreshToken) {
		return <CustomLoader />
	}

	// Ensure authAxios is ready before authenticated API queries mount.
	if (!storedAccessToken || !hasValidStoredAccessToken) {
		return <CustomLoader />
	}
	apiService.setupToken(storedAccessToken, ensureFreshSession)

	return (
		<AuthModeContext.Provider
			value={{
				mode: 'token',
				status: 'authenticated',
				initialized: true,
				authenticated: true,
				accessToken: storedAccessToken,
				refreshToken: storedRefreshToken,
				ensureFreshSession,
				logout,
			}}
		>
			{children}
		</AuthModeContext.Provider>
	)
}
